import { useEffect, useState } from "react";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import {
  parseDate,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";
import { ColumnsType } from "antd/es/table";
import Datatable from "../../../../core/common/dataTable";
import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
import { all_routes } from "../../../router/all_routes";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../../types/common-type";
import UserDetailsSection from "../UserDetailsSection";
import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
import Loading from "../../../../core/common/custom-loading/Loading";
import { useParams } from "react-router";
import { formattedDate } from "../../../../core/methods/formattedDate";
import { toast } from "react-toastify";
import DynamicSingleSelect, {
  FilterTypes,
  FilterErrors,
  initialFilters,
} from "../../reusable/DynamicSingleSelect";

const breadcrumbItem = [
  { label: "Manage Verifier" },
  { label: "Manage Verifier Cases" },
];

const Admin_ManageVerifierCases = () => {
  const { verifierId } = useParams();
  const { getLocalStorage } = useLocalStorage();

  // ================= STATES =================

  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [allCasesList, setAllCasesList] = useState<any[]>([]);

  const [currentVerifierData, setCurrentVerifierData] = useState<any>(null);

  const [managerVerifierData, setManagerVerifierData] = useState<any>(null);

  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);

  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});

  const [selectedDeleteItem, setSelectedDeleteItem] = useState<string | null>(
    null,
  );

  const [showCasesModal, setShowCasesModal] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  const [pagination_cases, setPagination_cases] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  // ================= API =================

  const { callApi: getAssignedCasesAPI, loading } = useApi<any>(
    webservice_url.get_cases_by_verifierId,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const { callApi: getCasesByFilterAPI, loading: loading_getFilteredCases } =
    useApi<any>(webservice_url.get_all_cases_by_filters, {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    });

  const { callApi: deleteCaseApi, loading: loading_delete } = useApi<any>("", {
    method: "DELETE",
    autoCall: false,
    handleGlobalError: false,
  });

  const { callApi: assignCasesAPI, loading: loading_assign } = useApi<any>(
    webservice_url.assign_case_to_verifier,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  // ================= DELETE ASSIGNED CASE =================

  const handleDeleteProgram = async (id: string) => {
    try {
      if (!id) return;

      const res = await deleteCaseApi(
        undefined,
        `${webservice_url.delete_case_from_verifier}/${id}`,
      );

      if (res?.success) {
        toast.success(res?.message || "Case deleted successfully");

        fetchData(pagination.current, pagination.pageSize);
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setSelectedDeleteItem(null);
    }
  };

  // ================= FETCH ASSIGNED CASES =================

  const fetchData = async (
    page: number = pagination.current,
    limit: number = pagination.pageSize,
  ) => {
    try {
      const res = await getAssignedCasesAPI({
        page,
        limit,
        member_id: verifierId ?? "",
      });

      if (res?.success) {
        const data = res?.data?.rows || [];

        setAllItemList(data);

        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: limit,
          total: res?.data?.pagination?.totalRecords || 0,
        }));
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  useEffect(() => {
    if (!verifierId) return;

    fetchData(1, pagination.pageSize);
  }, [verifierId]);

  // ================= ASSIGNED CASE PAGINATION =================

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  useEffect(() => {
    if (!verifierId) return;

    if (pagination.current === 1) return;

    fetchData(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  // ================= FILTER CHANGE =================

  const handleFilterChange = (name: string, value: string[]) => {
    setFilterData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }

      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    if (value?.length) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // ================= FETCH CASES BY FILTER =================

  const fetchSearchData = async (
    current: number,
    limit: number,
    filters: FilterTypes = filterData,
  ) => {
    try {
      const res = await getCasesByFilterAPI({
        status: "",
        page: current,
        limit,

        org_ids: filters?.organization ?? [],
        project_ids: filters?.project ?? [],
        location_ids: filters?.location ?? [],
        program_ids: filters?.program ?? [],
        department_ids: filters?.department ?? [],

        manager_id: managerVerifierData?.managerId ?? "",
      });

      if (res?.success) {
        const data = res?.data?.rows || [];

        setAllCasesList(data);

        setPagination_cases({
          current,
          pageSize: limit,
          total: res?.data?.pagination?.totalRecords || 0,
        });
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  // ================= SEARCH =================

  const handleSearch = async () => {
    const errors: FilterErrors = {};

    if (!filterData?.organization?.length) {
      errors.organization = "Organization is required";
    }

    if (!filterData?.project?.length) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setPagination_cases((prev) => ({
      ...prev,
      current: 1,
    }));

    await fetchSearchData(1, pagination_cases.pageSize, filterData);

    setSelectedRows([]);
    setShowCasesModal(true);
  };

  // ================= CLEAR FILTERS =================

  const handleClearAll = async () => {
    const clearedFilters: FilterTypes = {
      organization: [],
      project: [],
      location: [],
      program: [],
      department: [],
    };

    setFilterData(clearedFilters);
    setFilterErrors({});
    setAllCasesList([]);
    setSelectedRows([]);

    setPagination_cases((prev) => ({
      ...prev,
      current: 1,
      total: 0,
    }));
  };

  // ================= CASE TABLE PAGINATION =================

  const handleTableChange_cases = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination_cases.current ||
      pgConfig.pageSize !== pagination_cases.pageSize
    ) {
      setPagination_cases((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  useEffect(() => {
    if (!showCasesModal) return;

    if (pagination_cases.current === 1 && pagination_cases.pageSize === 10) {
      return;
    }

    fetchSearchData(
      pagination_cases.current,
      pagination_cases.pageSize,
      filterData,
    );
  }, [pagination_cases.current, pagination_cases.pageSize]);

  const handleAssignCase = async () => {
    console.log("filterData?.organization?.[0]", filterData?.organization?.[0]);
    if (!selectedRows?.length) {
      toast.error("Please select at least one case.");
      return;
    }
    console.log("Selected Rows Full Data:", selectedRows);

    selectedRows.forEach((row: any, index: number) => {
      console.log(`Selected Row ${index + 1}:`, row);
      console.log(`Row ${index + 1} Assignment ID:`, row.assignment_id);
      console.log(`Row ${index + 1} Project ID:`, row.project_id);
      console.log(`Row ${index + 1} Program ID:`, row.program_id);
      console.log(`Row ${index + 1} Department ID:`, row.department_id);
    });

    const cases = selectedRows.map((item: any) => ({
      case_id: item.assignment_id,
      package_id: item.package_id,
      project_id: item.project_id,
      program_id: item.program_id,
      department_id: item.department_id,
      org_id: item.org_id || filterData?.organization?.[0] || "",
    }));

    console.log("Final Cases Payload:", cases);

    const invalidCase = cases.find(
      (item: any) =>
        !item.project_id ||
        !item.program_id ||
        !item.department_id ||
        !item.org_id,
    );

    if (invalidCase) {
      console.error("Missing IDs in selected case:", invalidCase);
      toast.error(
        "Project, Program, Department, or Organization ID is missing. Please check the console.",
      );
      return;
    }

    const payload = {
      member_id: verifierId,
      cases,
    };

    console.log("Final Assign Cases Payload:", payload);

    try {
      const response = await assignCasesAPI(payload);

      if (response?.success) {
        toast.success(response?.message || "Cases assigned successfully.");

        setShowCasesModal(false);
        setSelectedRows([]);

        fetchData(pagination.current, pagination.pageSize);
      } else {
        console.error("Assignment API Error:", response);
        toast.error(
          response?.error || response?.message || "Unable to assign cases.",
        );
      }
    } catch (error: any) {
      console.error("Assign Cases Request Error:", error);

      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Something went wrong";

      toast.error(errMsg);
    }
  };

  // ================= ASSIGNED CASE COLUMNS =================

  const columns: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "serial_number",
      align: "center",
    },

    {
      title: "Candidate Name",
      dataIndex: "full_name",
      onCell: () => cellWidth(180),
      sorter: (a, b) => safeLocaleCompare(a, b, "full_name"),
    },

    {
      title: "Case Id",
      dataIndex: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: "Case Spoc",
      dataIndex: "case_spoc",
      onCell: () => cellWidth(140),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },

    {
      title: "Project",
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: "Program",
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: "Candidate Id",
      dataIndex: "hrms_id",
      onCell: () => cellWidth(160),
      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },

    {
      title: "Case Received Date",
      dataIndex: "case_received_date",
      onCell: () => cellWidth(200),
      render: (text: string) => {
        const date = formattedDate(text) || "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) =>
        parseDate(a.case_received_date).getTime() -
        parseDate(b.case_received_date).getTime(),
    },

    {
      title: "Due Date",
      dataIndex: "due_date",
      onCell: () => cellWidth(140),
      render: (text: string) => {
        const date = formattedDate(text) || "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) =>
        parseDate(a.due_date).getTime() - parseDate(b.due_date).getTime(),
    },

    {
      title: "Action",
      dataIndex: "",
      align: "center",

      render: (_text: string, record: any) => {
        const id = record?.member_case_map_id ?? "";

        return (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={loading_delete}
            onClick={() => {
              setSelectedDeleteItem(id);
              handleDeleteProgram(id);
            }}
          >
            {selectedDeleteItem === id ? (
              "Deleting..."
            ) : (
              <>
                <i className="ti ti-trash" /> Delete
              </>
            )}
          </button>
        );
      },
    },
  ];

  // ================= AVAILABLE CASE COLUMNS =================

  const columns_cases: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "sno",
      align: "center",
      render: (_text: string, _record: any, index: number) =>
        (pagination_cases.current - 1) * pagination_cases.pageSize + index + 1,
      sorter: false,
    },

    {
      title: "Candidate Name",
      dataIndex: "applicant_name",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
    },

    {
      title: "Case Id",
      dataIndex: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: "Case Spoc",
      dataIndex: "case_spoc",
      onCell: () => cellWidth(140),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },

    {
      title: "Project",
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: "Program",
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: "Candidate Id",
      dataIndex: "hrms_id",
      onCell: () => cellWidth(160),
      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },

    {
      title: "Total checks",
      dataIndex: "total_checks",
      onCell: () => cellWidth(160),
      sorter: (a, b) => safeLocaleCompare(a, b, "total_checks"),
    },

    {
      title: "Stopped",
      dataIndex: "stopped",
      onCell: () => cellWidth(160),
      sorter: (a, b) => safeLocaleCompare(a, b, "stopped"),
    },

    {
      title: "Due Date",
      dataIndex: "due_date",
      onCell: () => cellWidth(160),
      sorter: (a, b) => safeLocaleCompare(a, b, "due_date"),
      render: (text) => formattedDate(text),
    },

    {
      title: "Case Received Date",
      dataIndex: "case_received_date",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseDate(a.case_received_date).getTime() -
        parseDate(b.case_received_date).getTime(),
      render: (text) => formattedDate(text),
    },
  ];

  // ================= LOAD VERIFIER / MANAGER =================

  useEffect(() => {
    const storedData = getLocalStorage("admin_selected_verifier");

    if (!storedData) return;

    setCurrentVerifierData(storedData);

    setManagerVerifierData({
      name: storedData?.managerName,
      email: storedData?.managerEmail,
      mobile: storedData?.managerNumber,
      managerId: storedData?.managerId,
    });
  }, []);

  // ================= UI =================

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <PageBreadcrumb
            title="Manage Verifier Cases"
            breadcrumbs={breadcrumbItem}
            showBackButton={true}
            backRoute={all_routes.admin_manageVerifier}
          />

          {/* Verifier Details */}
          <div className="section-title">
            <span className="section-bar"></span>
            <h4>Verifier Details</h4>
          </div>

          <UserDetailsSection userDetails={currentVerifierData} />

          {/* Manager Details */}
          <div className="section-title">
            <span className="section-bar"></span>
            <h4>Manager Details</h4>
          </div>

          <UserDetailsSection userDetails={managerVerifierData} />

          <div className="pt-4"></div>

          {/* Filters */}

          <DynamicSingleSelect
            fields={[
              "organization",
              "project",
              "location",
              "program",
              "department",
            ]}
            filterData={filterData}
            onFilterDataChange={(name, value, _labels) => {
              handleFilterChange(name, value);
            }}
            parentError={filterErrors}
            showButton={true}
            buttonText={loading_getFilteredCases ? "Searching..." : "Search"}
            onButtonClick={handleSearch}
            disableButton={
              loading_getFilteredCases || loading || showCasesModal
            }
            onClearAll={handleClearAll}
          />

          {/* Assigned Cases Table */}

          <div
            className="card mt-3"
            style={{
              minHeight: "250px",
            }}
          >
            {loading ? (
              <Loading />
            ) : (
              <Datatable
                dataSource={allItemList}
                columns={columns}
                fixedHeader
                tableHeight={650}
                Selection={false}
                rowKey="member_case_map_id"
                pagination={pagination}
                handleTableChange={handleTableChange}
              />
            )}
          </div>

          {/* Assign Cases Modal */}

          {showCasesModal && (
            <div>
              {/* Backdrop */}
              <div className="modal-backdrop fade show"></div>

              {/* Modal */}
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="modalTitleId"
                aria-modal="true"
              >
                <div
                  className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl"
                  role="document"
                  style={{
                    width: "95%",
                    minWidth: "700px",
                    maxWidth: "800px",
                  }}
                >
                  <div className="modal-content w-100">
                    {/* Modal Header */}

                    <div className="modal-header">
                      <div
                        className="modal-title"
                        id="modalTitleId"
                        style={{
                          fontSize: "16px",
                        }}
                      >
                        Assign Cases
                      </div>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => {
                          setShowCasesModal(false);
                          setSelectedRows([]);
                        }}
                      />
                    </div>

                    {/* Modal Body */}

                    <div className="modal-body overflow-y">
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-tertiary py-1"
                          disabled={!selectedRows?.length || loading_assign}
                          onClick={handleAssignCase}
                        >
                          {loading_assign ? "Assigning..." : "+ Assign Cases"}
                        </button>
                      </div>

                      {/* Cases Table */}

                      <div className="card mt-3">
                        {loading_getFilteredCases ? (
                          <Loading />
                        ) : (
                          <Datatable
                            rowKey="case_id"
                            dataSource={allCasesList}
                            columns={columns_cases}
                            Selection={true}
                            fixedHeader
                            tableHeight={650}
                            onSelectionChange={(rows) => {
                              console.log("Datatable Selected Rows:", rows);

                              rows.forEach((row: any, index: number) => {
                                console.log(
                                  `Selected Row ${index + 1} Details:`,
                                  {
                                    assignment_id: row.assignment_id,
                                    case_id: row.case_id,
                                    package_id: row.package_id,
                                    org_id: row.org_id,
                                    project_id: row.project_id,
                                    program_id: row.program_id,
                                    department_id: row.department_id,
                                    project_name: row.project_name,
                                    program_name: row.program_name,
                                    department_name: row.department_name,
                                  },
                                );
                              });

                              setSelectedRows(rows);
                            }}
                            pagination={pagination_cases}
                            handleTableChange={handleTableChange_cases}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          .section-title {
            display: flex;
            align-items: center;
            gap: 12px;
            margin: 28px 0 18px;
          }

          .section-bar {
            width: 5px;
            height: 24px;
            background: #3f3d99;
            border-radius: 10px;
          }

          .section-title h4 {
            margin: 0;
            font-size: 24px;
            font-weight: 700;
            color: #243b6b;
            letter-spacing: 0.3px;
          }
        `}
      </style>
    </>
  );
};

export default Admin_ManageVerifierCases;

// import { useEffect, useState } from "react";

// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import {
//   parseDate,
//   safeLocaleCompare,
// } from "../../../../core/methods/sorterMethods";
// import { ColumnsType } from "antd/es/table";
// import Datatable from "../../../../core/common/dataTable";
// import { dummydata_verifier } from "../../../../core/data/js/dummy-data";
// import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
// import { all_routes } from "../../../router/all_routes";
// import DynamicSelectByManagerId from "../../reusable/DynamicSelectByManagerId";
// import useApi from "../../../../core/customhook/useApi";
// import webservice_url from "../../../../core/webservice/Webservice_url";
// import { PaginationType } from "../../../../types/common-type";
// import UserDetailsSection from "../UserDetailsSection";
// import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
// import Loading from "../../../../core/common/custom-loading/Loading";
// import { useParams } from "react-router";
// import { formattedDate } from "../../../../core/methods/formattedDate";
// import { toast } from "react-toastify";
// import DynamicSingleSelect from "../../reusable/DynamicSingleSelect";

// // breadcrumb data
// const breadcrumbItem = [
//   { label: "Manage Verifier" },
//   { label: "Manage Verifier Cases" },
// ];

// interface FilterType {
//   organization: string;
//   project: string;
//   location: string;
//   program: string;
//   department: string;
// }

// const initialFilter = {
//   organization: "",
//   project: "",
//   location: "",
//   program: "",
//   department: "",
// };

// const Admin_ManageVerifierCases = () => {
//   const { verifierId } = useParams();
//   const { getLocalStorage } = useLocalStorage();

//   // const [allItemList, setAllItemList] = useState<any[]>(dummydata_verifier);
//   const [allItemList, setAllItemList] = useState<any[]>([]);
//   const [allCasesList, setAllCasesList] = useState<any[]>([]);
//   const [currentVerifierData, setCurrentVerifierData] = useState<any>(null);
//   const [managerVerifierData, setManagerVerifierData] = useState<any>(null);

//   const [filterData, setFilterData] = useState<FilterType>(initialFilter);
//   const [selectedDeleteItem, setSelectedDeleteItem] = useState<string | null>(
//     null,
//   );

//   const [showCasesModal, setShowCasesModal] = useState<boolean>(false);
//   const [selectedRows, setSelectedRows] = useState<any[]>([]);

//   const [pagination, setPagination] = useState<PaginationType>({
//     current: 1,
//     pageSize: 1000, // limit of records
//     total: 0,
//   });
//   const [pagination_cases, setPagination_cases] = useState<PaginationType>({
//     current: 1,
//     pageSize: 1000, // limit of records
//     total: 0,
//   });

//   // ---- API ----
//   const { callApi: getAssignedCasesAPI, loading: loading } = useApi<any>(
//     webservice_url.get_cases_by_verifierId,
//     {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );
//   const { callApi: getCasesByFilterAPI, loading: loading_getFilteredCases } =
//     useApi<any>(webservice_url.get_all_cases_by_filters, {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     });
//   const { callApi: deleteCaseApi, loading: loading_delete } = useApi<any>("", {
//     method: "DELETE",
//     autoCall: false,
//     handleGlobalError: false,
//   });
//   const { callApi: assignCasesAPI, loading: loading_assign } = useApi<any>(
//     webservice_url.assign_case_to_verifier,
//     {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   // handleDelete
//   const handleDeleteProgram = async (id: string) => {
//     try {
//       // return;
//       const res = await deleteCaseApi(
//         undefined,
//         `${webservice_url.delete_case_from_verifier}/${id}`,
//       );
//       if (res?.success) {
//         toast.success(res?.message || "Program deleted successfully");
//         fetchData();
//       }
//     } catch (error: any) {
//       console.error(error);
//     } finally {
//       setSelectedDeleteItem(null);
//     }
//   };

//   // ======== fetch assigned cases =========
//   const fetchData = async (page = 1, limit = 10) => {
//     try {
//       const res = await getAssignedCasesAPI({
//         page,
//         limit,
//         member_id: verifierId ?? "",
//       });
//       if (res?.success) {
//         const data = res?.data?.rows || [];
//         setAllItemList(data);

//         setPagination((prev) => ({
//           ...prev,
//           total: res?.data?.pagination?.totalRecords || 0,
//         }));
//       }
//     } catch (error: any) {
//       console.error(error?.response);
//     }
//   };

//   useEffect(() => {
//     if (!verifierId) return;
//     fetchData();
//   }, [verifierId, pagination?.current, pagination?.pageSize]);

//   // handleTableChange
//   const handleTableChange = (pgConfig: any) => {
//     if (
//       pgConfig.current !== pagination.current ||
//       pgConfig.pageSize !== pagination.pageSize
//     ) {
//       setPagination((prev) => ({
//         ...prev,
//         current: pgConfig.current,
//         pageSize: pgConfig.pageSize,
//       }));
//     }
//   };
//   // ======== / fetch assigned cases =========

//   // ======== fetch cases by filters =========
//   const fetchSearchData = async (current: number, limit: number) => {
//     try {
//       const res = await getCasesByFilterAPI({
//         org_id: filterData?.organization ?? "",
//         project_id: filterData?.project ?? "",
//         program_id: filterData?.program ?? "",
//         department_id: filterData?.department ?? "",
//         manager_id: managerVerifierData?.managerId ?? "",

//         page: current,
//         limit: limit,
//       });
//       if (res?.success) {
//         const data = res?.data?.rows || [];
//         setAllCasesList(data);

//         setPagination_cases({
//           current: current,
//           pageSize: limit,
//           total: res?.data?.pagination?.totalRecords || 0,
//         });

//         // setShowCasesModal(true);
//       }
//     } catch (error: any) {
//       console.error(error?.response);
//     }
//   };

//   const handleTableChange_cases = (pgConfig: any) => {
//     if (
//       pgConfig.current !== pagination.current ||
//       pgConfig.pageSize !== pagination.pageSize
//     ) {
//       setPagination_cases((prev) => ({
//         ...prev,
//         current: pgConfig.current,
//         pageSize: pgConfig.pageSize,
//       }));
//     }
//   };
//   // ======== / fetch cases by filters =========

//   // handleAssignCases
//   const handleAssignCase = async () => {
//     const payload = {
//       member_id: verifierId,
//       cases: selectedRows.map((item) => ({
//         case_id: item.assignment_id,
//         package_id: item.package_id,
//         project_id: filterData?.project,
//         program_id: filterData?.program,
//         department_id: filterData?.department,
//         org_id: filterData?.organization,
//       })),
//     };

//     try {
//       const response = await assignCasesAPI(payload);
//       if (response?.success) {
//         toast.success(response?.message || "Cases assigned successfully.");

//         // Optional: refresh the table
//         fetchData(pagination.current, pagination.pageSize);

//         // Optional: clear selected rows
//         setShowCasesModal(false);
//         setSelectedRows([]);
//       }
//     } catch (error: any) {
//       console.error(error);
//       const errMsg = error?.response?.data?.error || "Something went wrong";
//       toast.error(errMsg);
//     }
//   };

//   // ---- Columns ----
//   const columns: ColumnsType<any> = [
//     {
//       title: "S.No",
//       dataIndex: "sno",
//       align: "center",
//       render: (text: string, record: any, index: number) => index + 1,
//       sorter: false,
//     },
//     {
//       title: "Candidate Name",
//       dataIndex: "full_name",
//       onCell: () => cellWidth(180),
//       sorter: (a, b) => safeLocaleCompare(a, b, "full_name"),
//     },
//     {
//       title: "Case Id",
//       dataIndex: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
//     },
//     {
//       title: "Case Spoc",
//       dataIndex: "case_spoc",
//       onCell: () => cellWidth(140),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
//     },
//     {
//       title: "Project",
//       dataIndex: "project_name",
//       key: "project_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.project_name ?? "").localeCompare(b.project_name ?? ""),
//     },
//     {
//       title: "Program",
//       dataIndex: "program_name",
//       key: "program_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.program_name ?? "").localeCompare(b.program_name ?? ""),
//     },
//     {
//       title: "Department",
//       dataIndex: "department_name",
//       key: "department_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.department_name ?? "").localeCompare(b.department_name ?? ""),
//     },
//     {
//       title: "Candidate Id",
//       dataIndex: "hrms_id",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
//     },
//     {
//       title: "Case Received Date",
//       dataIndex: "case_received_date",
//       onCell: () => cellWidth(200),
//       render: (text: string) => {
//         const date = formattedDate(text) || "-";
//         return <div>{date}</div>;
//       },
//       sorter: (a: any, b: any) =>
//         parseDate(a.case_received_date).getTime() -
//         parseDate(b.case_received_date).getTime(),
//     },
//     {
//       title: "Due Date",
//       dataIndex: "due_date",
//       onCell: () => cellWidth(140),
//       render: (text: string) => {
//         const date = formattedDate(text) || "-";
//         return <div>{date}</div>;
//       },
//       sorter: (a: any, b: any) =>
//         parseDate(a.due_date).getTime() - parseDate(b.due_date).getTime(),
//     },
//     {
//       title: "Action",
//       dataIndex: "",
//       align: "center",
//       render: (text: string, record: any) => {
//         return (
//           <button
//             type="button"
//             className="btn btn-sm btn-danger"
//             disabled={loading_delete}
//             onClick={() => {
//               const id = record?.member_case_map_id ?? "";
//               handleDeleteProgram(id);
//               setSelectedDeleteItem(id);
//             }}
//           >
//             {selectedDeleteItem == record?.member_case_map_id ? (
//               "Deleting..."
//             ) : (
//               <>
//                 <i className="ti ti-trash"></i> Delete
//               </>
//             )}
//             {/* <i className="ti ti-trash"></i> */}
//           </button>
//         );
//       },
//     },
//   ];

//   const columns_cases: ColumnsType<any> = [
//     {
//       title: "S.No",
//       dataIndex: "sno",
//       align: "center",
//       render: (text: string, record: any, index: number) => index + 1,
//       sorter: false,
//     },
//     {
//       title: "Candidate Name",
//       dataIndex: "applicant_name",
//       onCell: () => cellWidth(200),
//       sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
//     },
//     {
//       title: "Case Id",
//       dataIndex: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
//     },
//     {
//       title: "Case Spoc",
//       dataIndex: "case_spoc",
//       onCell: () => cellWidth(140),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
//     },
//     {
//       title: "Project",
//       dataIndex: "project_name",
//       key: "project_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.project_name ?? "").localeCompare(b.project_name ?? ""),
//     },
//     {
//       title: "Program",
//       dataIndex: "program_name",
//       key: "program_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.program_name ?? "").localeCompare(b.program_name ?? ""),
//     },
//     {
//       title: "Department",
//       dataIndex: "department_name",
//       key: "department_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.department_name ?? "").localeCompare(b.department_name ?? ""),
//     },
//     {
//       title: "Candidate Id",
//       dataIndex: "hrms_id",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
//     },
//     {
//       title: "Total checks",
//       dataIndex: "total_checks",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "total_checks"),
//     },
//     {
//       title: "Stopped",
//       dataIndex: "stopped",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "stopped"),
//     },
//     {
//       title: "Due Date",
//       dataIndex: "due_date",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "due_date"),
//       render: (text) => formattedDate(text),
//     },
//     {
//       title: "Case Received Date",
//       dataIndex: "case_received_date",
//       onCell: () => cellWidth(200),
//       sorter: (a: any, b: any) =>
//         parseDate(a.case_received_date).getTime() -
//         parseDate(b.case_received_date).getTime(),
//       render: (text) => formattedDate(text),
//     },
//   ];

//   useEffect(() => {
//     const storedData = getLocalStorage("admin_selected_verifier");
//     if (!storedData) return;
//     setCurrentVerifierData(storedData);
//     setManagerVerifierData(() => ({
//       name: storedData?.managerName,
//       email: storedData?.managerEmail,
//       mobile: storedData?.managerNumber,
//       managerId: storedData?.managerId,
//     }));
//   }, []);

//   return (
//     <>
//       <div className="page-wrapper">
//         <div className="content">
//           {/* PageBreadcrumb */}
//           <PageBreadcrumb
//             title="Manage Verifier Cases"
//             breadcrumbs={breadcrumbItem}
//             showBackButton={true}
//             backRoute={all_routes.admin_manageVerifier}
//             // showExport={true}
//           />
//           {/* /PageBreadcrumb */}

//           {/* verifier details */}
//           <div className="section-title">
//             <span className="section-bar"></span>
//             <h4>Verifier Details</h4>
//           </div>
//           <UserDetailsSection userDetails={currentVerifierData} />

//           {/* manager details */}
//           <div className="section-title">
//             <span className="section-bar"></span>
//             <h4>Manager Details</h4>
//           </div>
//           <UserDetailsSection userDetails={managerVerifierData} />

//           <div className="pt-4"></div>
//           {/* filters */}
//           {/* <DynamicSelectByManagerId
//             managerId={managerVerifierData?.managerId}
//             fields={[
//               "organization",
//               "project",
//               "location",
//               "program",
//               "department",
//             ]}
//             filterData={filterData}
//             onFilterDataChange={(name, value, label) => {
//               setFilterData((prev) => ({ ...prev, [name]: value }));
//             }}
//             colClass={"col-6 col-sm-3 col-md-3 col-lg-2"}
//             showButton={true}
//             buttonText={loading_getFilteredCases ? "Searching..." : "Search"}
//             disableButton={!filterData?.department || loading || showCasesModal}
//             onButtonClick={() => {
//               fetchSearchData(pagination?.current, pagination?.pageSize);
//               setShowCasesModal(true);
//             }}
//           /> */}
//           <DynamicSingleSelect
//         fields={[
//           "organization",
//           "project",
//           "location",
//           "program",
//           "department",
//         ]}
//         filterData={filterData}
//         onFilterDataChange={(
//           name,
//           value,
//           _labels
//         ) => {
//           handleFilterChange(
//             name,
//             value
//           );
//         }}
//         parentError={filterErrors}
//         showButton={true}
//         buttonText="Search"
//         onButtonClick={
//           handleSearch
//         }
//         disableButton={loading}
//         onClearAll={
//           handleClearAll
//         }
//       />

//           {/* Table  */}
//           <div className="card mt-3" style={{ minHeight: "250px" }}>
//             {loading ? (
//               <Loading />
//             ) : (
//               <Datatable
//                 dataSource={allItemList}
//                 columns={columns}
//                 Selection={false}
//                 rowKey="sno"
//                 pagination={pagination}
//                 handleTableChange={handleTableChange}
//               />
//             )}
//           </div>

//           {/* cases modal */}
//           {showCasesModal && (
//             <div>
//               {/* Backdrop */}
//               <div className="modal-backdrop fade show"></div>

//               {/* Modal */}
//               <div
//                 className="modal fade show d-block"
//                 tabIndex={-1}
//                 role="dialog"
//                 aria-labelledby="modalTitleId"
//                 aria-modal="true"
//               >
//                 <div
//                   className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl"
//                   role="document"
//                   style={{
//                     width: "95%",
//                     minWidth: "700px",
//                     maxWidth: "800px",
//                   }}
//                 >
//                   <div className="modal-content w-100">
//                     <div className="modal-header">
//                       <div
//                         className="modal-title"
//                         id="modalTitleId"
//                         style={{ fontSize: "16px" }}
//                       >
//                         Assign Cases
//                       </div>
//                       <button
//                         type="button"
//                         className="btn-close"
//                         onClick={() => {
//                           setShowCasesModal(false);
//                           setSelectedRows([]);
//                         }}
//                       />
//                     </div>

//                     <div className="modal-body overflow-y">
//                       <div className="text-end">
//                         <button
//                           type="button"
//                           className="btn btn-tertiary py-1"
//                           disabled={!selectedRows?.length}
//                           onClick={handleAssignCase}
//                         >
//                           {loading_assign ? "Assigning..." : "+ Assign Cases"}
//                         </button>
//                       </div>

//                       {/* Table  */}
//                       <div className="card mt-3">
//                         {loading_getFilteredCases ? (
//                           <Loading />
//                         ) : (
//                           <Datatable
//                             rowKey="case_id"
//                             dataSource={allCasesList}
//                             columns={columns_cases}
//                             Selection={true}
//                             onSelectionChange={(rows) => {
//                               setSelectedRows(rows);
//                             }}
//                             pagination={pagination_cases}
//                             handleTableChange={handleTableChange_cases}
//                           />
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//       <style>
//         {`
//             .section-title {
//                 display: flex;
//                 align-items: center;
//                 gap: 12px;
//                 margin: 28px 0 18px;
//                 }

//                 .section-bar {
//                 width: 5px;
//                 height: 24px;
//                 background: #3f3d99;
//                 border-radius: 10px;
//                 }

//                 .section-title h3 {
//                 margin: 0;
//                 font-size: 24px;
//                 font-weight: 700;
//                 color: #243b6b;
//                 letter-spacing: 0.3px;
//                 }`}
//       </style>
//     </>
//   );
// };

// export default Admin_ManageVerifierCases;
